import { useQuery } from "@tanstack/react-query"
import { apiFetch } from "./utils"

export interface SeriesPerformance {
  called_pct: number | null
  called: number | null
  unfunded: number | null
  dist: number | null
  dpi: number | null
  rvpi: number | null
  mv: number | null
  tvpi: number | null
  irr: number | null
  age: number | null
  mv_pct?: number | null
  pl_pct?: number | null
  pl?: number | null
}

export interface QuarterActivity {
  net_cf: number | null
  pl: number | null
  delta_mv: number | null
  delta_mv_pct: number | null
  called_pct: number | null
  called: number | null
  dist: number | null
  irr_change?: number | null
  qoq_pl_pct?: number | null
}

export interface YtdPerformance {
  called_pct: number | null
  called: number | null
  dist: number | null
  delta_mv: number | null
  net_cf: number | null
  pl: number | null
}

export interface SnapshotAsset {
  portfolio_company_id: string | null
  name: string
  sector: string | null
  geographic_region?: string | null
  cost: number
  fair_value: number
  distribution: number
  dist_itd: number
  tvpi: number | null
  pl: number
  is_other?: boolean
}

export interface SeriesRow {
  security_id: string
  series: string
  vintage: number
  asset_class: string
  asset_class_label: string
  sector: string | null
  stage: string | null
  commitment: number
  performance: SeriesPerformance
  quarter_activity: QuarterActivity
  ytd_performance: YtdPerformance
  snapshot_id: string | null
  net_snapshot_id: string | null
  snapshot_url: string | null
  net_snapshot_url: string | null
  assets?: SnapshotAsset[]
}

export interface GroupRow {
  label: string
  series_count: number
  commitment: number
  performance: SeriesPerformance
  quarter_activity: QuarterActivity
  ytd_performance: YtdPerformance
}

export type Perspective = "net" | "gross"

export interface KpiSummary {
  nav: number | null
  irr: number | null
  moic: number | null
  realized_pl_q: number | null
  unrealized_pl_q: number | null
  prior_nav: number | null
  prior_irr: number | null
  prior_moic: number | null
  prior_realized_pl_q: number | null
  prior_unrealized_pl_q: number | null
}

export interface AiSummaryData {
  high_level_summary: string
  attribution_highlights: string[]
  liquidity_events: string[]
  new_investments: string[]
  cash_flows: string
}

export interface SnapshotSummaryData {
  perspective: Perspective
  period: { year: number; quarter: number; label: string }
  series: SeriesRow[]
  totals: GroupRow
  by_vintage: GroupRow[]
  by_asset_class: GroupRow[]
  by_stage: GroupRow[]
  by_sector: GroupRow[]
  status: "Complete" | "Pending"
  // Present when the cache is being built asynchronously (HTTP 202)
  building?: true
  kpi_summary?: KpiSummary
  geographic_exposure?: { region: string; mv: number }[]
  ai_summary?: AiSummaryData | null
}

export const fetchSnapshotSummary = (
  year: number,
  quarter: number,
  perspective: Perspective = "net",
) =>
  apiFetch<SnapshotSummaryData>(
    `/api/snapshot_summaries/${year}/${quarter}?perspective=${perspective}`,
  )

export const useSnapshotSummary = (
  year: number,
  quarter: number,
  perspective: Perspective = "net",
) =>
  useQuery<SnapshotSummaryData, Error>({
    queryKey: ["snapshot_summary", year, quarter, perspective],
    queryFn: () => fetchSnapshotSummary(year, quarter, perspective),
    // Poll every 3 seconds while the cache is being built
    refetchInterval: (data) => (data?.building ? 3000 : false),
  })
